import { LocationId } from '../location';
import { MonthOneBased, HasId, getUTCMonthOneBased, getMoment } from '../common';
import { tuple } from '../common/lang/tuple';

const moment = getMoment();

export const ALL_MONTHLY_STATS_ROW_TYPES = tuple('total', 'movement');

export type AllMonthlyStatsRowTypes = typeof ALL_MONTHLY_STATS_ROW_TYPES;

export type MonthlyStatsRowType = AllMonthlyStatsRowTypes[number];

export interface MonthlyStatsPeriod {
    month: MonthOneBased;
    year: number;
}

export interface LocationPeriod extends MonthlyStatsPeriod {
    locationId: LocationId;
}

export interface LocationPeriodRowType extends LocationPeriod {
    rowType: MonthlyStatsRowType;
}

export interface LocationPeriodOptionalRowType extends LocationPeriod {
    rowType?: MonthlyStatsRowType;
}

export interface MonthlyStats extends HasId {
    locationId: LocationId;
    month: MonthOneBased;
    year: number;
    rowType: MonthlyStatsRowType;
}

/**
 * Returns the UTC Calendar Month + Year from a date.
 * Note that months are returned indexed from 1
 * e.g.
 *   getUTCPeriodFromDate(new Date('2017-01-01T00:00:00.000Z')) = { month: 1, year: 2017 }
 *   getUTCPeriodFromDate(new Date('2016-12-31T00:00:00.000Z')) = { month: 12, year: 2016 }
 *   getUTCPeriodFromDate(new Date('2017-01-01T00:00:00+1100')) = { month: 12, year: 2016 }
 *
 * @param date - the date
 * @returns The monthly stats period
 */
export function getPeriodFromDateUTC(date: Date): MonthlyStatsPeriod {
    return {
        month: getUTCMonthOneBased(date),
        year: date.getUTCFullYear(),
    };
}

export function getPeriodsFromRange(
    startPeriodInclusive: MonthlyStatsPeriod,
    endPeriodInclusive: MonthlyStatsPeriod,
): MonthlyStatsPeriod[] {
    let period = {
        month: startPeriodInclusive.month,
        year: startPeriodInclusive.year,
    };

    const periods: MonthlyStatsPeriod[] = [];

    while (
        period.year < endPeriodInclusive.year ||
        (period.year === endPeriodInclusive.year && period.month <= endPeriodInclusive.month)
    ) {
        periods.push(period);
        period = incrementPeriod(period, 1);
    }

    return periods;
}

export function getPeriodsFromDateRangeInclusiveUTC(startDate: Date, endDate: Date): MonthlyStatsPeriod[] {
    const startPeriod = getPeriodFromDateUTC(startDate);
    const endPeriod = getPeriodFromDateUTC(endDate);
    const result = getPeriodsFromRange(startPeriod, endPeriod);
    return result;
}

export function incrementPeriod(period: MonthlyStatsPeriod, monthsToAdd: number): MonthlyStatsPeriod {
    const result = {
        month: period.month,
        year: period.year,
    };

    const direction = monthsToAdd > 0 ? 1 : -1;

    /*
     * Implemented as loop to save dev time (a crayon once went too far
     * Up my nose, forever preventing me from performing basic math)
     * Feel free to convert to pure math using mod operator
     */
    while (monthsToAdd) {
        if (direction > 0) {
            if (result.month === 12) {
                result.year++;
                result.month = 1;
            } else {
                result.month++;
            }
        } else if (result.month === 1) {
            result.month = 12;
            result.year--;
        } else {
            result.month--;
        }
        // eslint-disable-next-line no-param-reassign
        monthsToAdd -= direction;
    }

    return result;
}

export function getStartOfMonthUTC(period: MonthlyStatsPeriod): Date {
    const result = new Date(Date.UTC(period.year, period.month - 1, 1));
    return result;
}

export function getEndOfMonthUTC(period: MonthlyStatsPeriod): Date {
    const startOfMonth = getStartOfMonthUTC(period);
    const result = moment(startOfMonth).utc().endOf('month').endOf('day').toDate();
    return result;
}
